import i18n from '@/i18n';
import { bumpImageSpaceUsageCounts } from '@/utils/api';
import {
  isImageSpaceClientLocalId,
  shouldSkipImageSpaceServerLookup,
} from '@/utils/imageSpaceImportId';
import {
  buildVideoConfigPayload,
  resolveVideoSubmitPrompt,
} from '@/pages/ipe/workbench/components/VideoTaskStudioFields';
import {
  VIDEO_DEFAULT_ASPECT_RATIO,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_DEFAULT_GENERATION_MODE,
  VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
  VIDEO_DEFAULT_SEED,
  VIDEO_I2V_SUPPLEMENT_MAX_LENGTH,
  VIDEO_T2V_PROMPT_MAX_LENGTH,
  isTextToVideoNav,
  normalizeVideoAspectRatio,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoModelPromptType,
  normalizeVideoSeed,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';
import { assertVideoToolsAvailable } from '@/pages/ipe/workbench/constants/videoToolsMaintenance';
import { resolveVideoT2vWorkflowPrompt } from './videoT2vPromptCaseLoader';
import {
  extractVideoUrlsFromFundingTask,
  waitForFundingTaskComplete,
} from './waitForFundingTask';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import { beginWorkflowFeatureTask } from './workflowFeatureTask';

/** 与后端 agent-video pollWallMaxMs 对齐：最长等待 2 小时 */
const VIDEO_WAIT_TIMEOUT_MS = 7200000;
const VIDEO_POLL_INTERVAL_MS = 5000;

/** 与 VideoGeneratePage 单图模式一致 */
const VIDEO_I2V_UPLOAD_MODE_SINGLE = 'single';

/**
 * 仅传后端图库可查询的 id。工作流合成图常落成 wf-goods-ai-*，
 * 若仍是占位/落盘中/已知 404，勿写入 imageIds，避免 Agent/Dress 误用空图库记录。
 */
function resolveWorkflowVideoImageIds(spaceId) {
  const id = String(spaceId || '').trim();
  if (!id || isImageSpaceClientLocalId(id) || shouldSkipImageSpaceServerLookup(id)) {
    return [];
  }
  return [id];
}

/**
 * 工作流批量：图生/文生视频节点。
 * 创建 funding 任务（创建时扣费并由 Worker 拉起 agent 视频管线），轮询至终态后返回成片 URL。
 * 不改动流水线当前图（与 AI 文案类似，视频为旁路产出）。
 *
 * 图生 payload 对齐 VideoGeneratePage 单图模式（uploadMode=single + 显式 firstFrameUrl），
 * 避免工作流少传字段 / 混入展示态 URL 导致与手动页行为不一致。
 */
export async function runVideoWorkflowStep({
  row,
  currentSpaceId,
  currentUrl,
  params = {},
  workflowContext = {},
  navId = 'videos',
}) {
  const maintenance = assertVideoToolsAvailable();
  if (!maintenance.ok) {
    throw new Error(maintenance.message);
  }
  const isT2V = isTextToVideoNav(navId);
  const typeLabel = isT2V ? '文生视频' : '图生视频';

  let firstFrameUrl = '';
  if (!isT2V) {
    if (!currentUrl) {
      throw new Error(i18n.t('workbench:ipeUtilError.i2vNeedFirstFrame'));
    }
    firstFrameUrl = await resolveImg2ImgReferenceForApi(currentUrl);
    if (!firstFrameUrl) {
      throw new Error(i18n.t('workbench:ipeUtilError.i2vFirstFrameUnresolved'));
    }
  }

  const generationMode = normalizeVideoGenerationMode(
    params.generationMode || VIDEO_DEFAULT_GENERATION_MODE,
  );
  const aspectRatio = normalizeVideoAspectRatio(params.aspectRatio || VIDEO_DEFAULT_ASPECT_RATIO);
  const durationSec = normalizeVideoDurationSec(params.durationSec ?? VIDEO_DEFAULT_DURATION_SEC);
  const seed = normalizeVideoSeed(params.seed ?? VIDEO_DEFAULT_SEED);
  const supplementPrompt = String(params.supplementPrompt || '')
    .trim()
    .slice(0, VIDEO_I2V_SUPPLEMENT_MAX_LENGTH);
  const modelPromptType = normalizeVideoModelPromptType(
    params.modelPromptType || VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
  );
  const rawModelImageUrl = String(params.modelImageUrl || '').trim();
  let modelImageUrl = '';
  let modelImagePreviewUrl = '';
  if (!isT2V && rawModelImageUrl) {
    modelImageUrl = await resolveImg2ImgReferenceForApi(rawModelImageUrl);
    if (!modelImageUrl) {
      throw new Error(i18n.t('workbench:ipeUtilError.modelRefUnresolved'));
    }
    modelImagePreviewUrl = String(params.modelImagePreviewUrl || modelImageUrl).trim() || modelImageUrl;
  }
  const hasModelImage = Boolean(modelImageUrl);
  const scriptPrompt = isT2V
    ? (await resolveVideoT2vWorkflowPrompt(params)).slice(0, VIDEO_T2V_PROMPT_MAX_LENGTH)
    : '';
  const resolvedPrompt = resolveVideoSubmitPrompt({
    isT2V,
    scriptPrompt,
    supplementPrompt,
    modelPromptType,
    hasModelImage,
  });
  if (isT2V && !resolvedPrompt) {
    throw new Error(i18n.t('workbench:ipeUtilError.t2vNeedPrompt'));
  }
  if (!isT2V && !resolvedPrompt) {
    throw new Error(i18n.t('workbench:ipeUtilError.i2vEmptyPrompt'));
  }
  const configPayload = buildVideoConfigPayload({
    genMode: generationMode,
    aspectRatio,
    durationSec,
    seed,
    ...(isT2V
      ? { modelPromptType }
      : {
        supplementPrompt,
        modelImageUrl: hasModelImage ? modelImageUrl : undefined,
        modelImagePreviewUrl: hasModelImage ? modelImagePreviewUrl : undefined,
      }),
  });

  const imageIds = (!isT2V) ? resolveWorkflowVideoImageIds(currentSpaceId) : [];

  const { taskId } = await beginWorkflowFeatureTask({
    taskType: isT2V ? 'textToVideo' : 'videos',
    taskName: isT2V ? '文生视频任务' : '图生视频任务',
    imageIds,
    // 与手动页一致：任务头预览用已 resolve 的首帧，勿混入仅展示态的 currentUrl
    previewUrl: isT2V ? '' : firstFrameUrl,
    estimatedTotal: 1,
    workflowContext,
    initialResult: {
      ...configPayload,
      generationType: isT2V ? 't2v' : 'i2v',
      scriptPrompt: resolvedPrompt,
      prompt: resolvedPrompt,
      ...(isT2V ? {} : {
        // 对齐 VideoGeneratePage 单图模式字段，勿用 buildPipelineReferenceResult 把 currentUrl 写进 preview
        uploadMode: VIDEO_I2V_UPLOAD_MODE_SINGLE,
        firstFrameUrl,
        referenceImageUrl: firstFrameUrl,
        imageUrl: firstFrameUrl,
        previewUrl: firstFrameUrl,
        referenceUrls: [firstFrameUrl],
        referencePreviews: [firstFrameUrl],
        referenceImages: [firstFrameUrl],
      }),
      taskNote: String(params.taskNote || '').trim() || undefined,
      resultTag: String(params.resultTag || '').trim() || undefined,
      serverWorker: true,
    },
  });

  if (imageIds.length > 0) {
    try {
      await bumpImageSpaceUsageCounts(imageIds);
    } catch {
      /* ignore */
    }
  }

  const task = await waitForFundingTaskComplete(taskId, {
    intervalMs: VIDEO_POLL_INTERVAL_MS,
    timeoutMs: VIDEO_WAIT_TIMEOUT_MS,
  });
  try {
    const { setWorkflowAwaitingFundingTask } = await import('@/pages/ipe/workbench/constants/workflowTaskQueue');
    const wfId = String(workflowContext?.workflowTaskId || '').trim();
    if (wfId) setWorkflowAwaitingFundingTask(wfId, null);
  } catch {
    /* ignore */
  }
  const status = String(task?.status || '').toLowerCase();
  if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
    const hint = task?.result?.failureHint
      || task?.result?.aigcError
      || task?.result?.agentVideoTerminatedReason
      || task?.result?.error;
    throw new Error(hint || i18n.t('workbench:ipeUtilError.taskFailedNamed', { name: typeLabel }));
  }
  const videoUrls = extractVideoUrlsFromFundingTask(task);
  if (videoUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.videoDoneNoUrl', { name: typeLabel }));
  }

  return {
    spaceId: currentSpaceId,
    imageUrl: currentUrl,
    taskId,
    taskIds: [taskId],
    videoUrls,
    outputVideoUrl: videoUrls[0] || '',
  };
}
